import { useEffect, useRef, useState } from 'react';
import { Overlay } from '@/components/Overlay';
import type { AudioTrack, GenerationDto, VersionDto } from '@str/api/types';
import { newCardEdit } from '@str/shared';
import {
  clampTrim,
  checkReelAudio,
  checkReelSize,
  reelSource,
  reelTracks,
  generateReelMusic,
  renderReel,
  reelFilmstrip,
  reelFrame,
  reelDraft,
  sameReelEdit,
  type ReelDraft,
} from '@/lib/reel';
import { exportOverlay } from '@/lib/picture-render';
import { errorMessage, uploadFile } from '@/lib/api';
import { corsSource } from '@/lib/cors-source';
import { readReelClip } from '@/lib/composer-media';
import {
  effectiveClips,
  enterSequence,
  outputPoint,
  reconcileSequence,
  sequenceDuration,
  fadeOpacity,
  type ReelClip,
} from '@/lib/reel-sequence';
import { reelAudioPreview } from '@/lib/reel-audio-preview';
import { ReelClips } from './ReelClips';
import { ReelTextTiming } from './ReelTextTiming';
import { ReelCaptions } from './ReelCaptions';
import { ReelSound } from './ReelSound';
import { defaultReelSound } from '@/lib/reel-sound';
import { ErrorText } from '@/components/ui';
import { PictureStage } from './PictureStage';
import { PictureTools } from './PictureTools';
import './composer.css';

export type ReelTool = 'Clips' | 'Text' | 'Captions' | 'Cover' | 'Music';
type Props = {
  postId: string;
  propertyId: string;
  /** Which tool the sheet opens on; the Music chip deep-links to the soundtrack. */
  initialTool?: ReelTool;
  version: VersionDto;
  initial: ReelDraft;
  originalSource?: string;
  onAccepted: (job: GenerationDto) => void;
  onPreserve: (draft: ReelDraft) => void;
  onCancel: () => void;
};
const stamp = (ms: number) => `${(ms / 1000).toFixed(1)}s`;
export function ReelSheet({
  postId,
  propertyId,
  initialTool,
  version,
  initial,
  originalSource,
  onAccepted,
  onPreserve,
  onCancel,
}: Props) {
  const [draft, setDraft] = useState(initial);
  const [tool, setTool] = useState<ReelTool>(initialTool ?? 'Clips');
  const [duration, setDuration] = useState(0),
    [frame, setFrame] = useState(''),
    [frames, setFrames] = useState<string[]>([]);
  const [previewOverlay, setPreviewOverlay] = useState<{
    layers: ReelDraft['layers'];
    timings: ReelDraft['layerTimings'];
    items: { url: string; layerId?: string }[];
  } | null>(null);
  const [clipId, setClipId] = useState(initial.clips?.[0]?.id ?? 'original');
  const [playIndex, setPlayIndex] = useState(0);
  const [outputMs, setOutputMs] = useState(0);
  const [statuses, setStatuses] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState('');
  const [preparing, setPreparing] = useState(false);
  const sourceDurations = useRef<Record<string, number>>({});
  const audioGraph = useRef<ReturnType<typeof reelAudioPreview> | null>(null);
  const draftRef = useRef(draft);
  const alive = useRef(true);
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      audioGraph.current?.close();
    };
  }, []);
  const [busy, setBusy] = useState(false),
    [playing, setPlaying] = useState(false),
    [error, setError] = useState<string | null>(null);
  const [tracks, setTracks] = useState<AudioTrack[]>([]);
  const [making, setMaking] = useState(false);
  const [selected, setSelected] = useState<string | null>(null),
    [editing, setEditing] = useState<string | null>(null),
    [color, setColor] = useState('#ffffff');
  const video = useRef<HTMLVideoElement>(null),
    cover = useRef<HTMLVideoElement>(null),
    audio = useRef<HTMLAudioElement>(null),
    dialog = useRef<HTMLDivElement>(null),
    audioInput = useRef<HTMLInputElement>(null);
  const original = originalSource ?? reelSource(version);
  const clips = effectiveClips(draft);
  const selectedClip = clips.find((c) => c.id === clipId) ?? clips[0]!;
  const currentClip = playing ? (clips[playIndex] ?? clips[0]!) : selectedClip;
  const clipSource = (clip: ReelClip) =>
    clip.source.kind === 'original' ? original : `/api/media/${clip.source.mediaId}/file`;
  const [localClips, setLocalClips] = useState<Record<string, string>>({});
  useEffect(() => {
    const urls = Object.fromEntries(
      Object.entries(draft.clipFiles ?? {}).map(([id, file]) => [id, URL.createObjectURL(file)]),
    );
    setLocalClips(urls);
    return () => Object.values(urls).forEach((url) => URL.revokeObjectURL(url));
  }, [draft.clipFiles]);
  const source = localClips[currentClip.id] ?? clipSource(currentClip);
  const selectedSource = localClips[selectedClip.id] ?? clipSource(selectedClip);
  const total = sequenceDuration(clips);
  const coverOutput = draft.clips ? draft.coverMs : draft.coverMs - draft.trim.startMs;
  const coverPoint = outputPoint(clips, coverOutput);
  const coverOpacity = fadeOpacity(clips, coverOutput, draft.transition);
  const coverSource =
    localClips[clips[coverPoint.index]!.id] ?? clipSource(clips[coverPoint.index]!);
  const updateDraft = (next: ReelDraft) => {
    draftRef.current = next;
    setDraft(next);
  };
  const patchLayers = (layers: ReelDraft['layers']) =>
    updateDraft({
      ...draft,
      layers,
      ...(draft.layerTimings
        ? { layerTimings: draft.layerTimings.filter((t) => layers.some((l) => l.id === t.layerId)) }
        : {}),
    });
  const replacement = Boolean(draft.audioFile || draft.audio);
  useEffect(() => {
    const prior = document.activeElement as HTMLElement | null;
    dialog.current?.focus();
    return () => prior?.focus();
  }, []);
  useEffect(() => {
    let active = true;
    void reelTracks(propertyId)
      .then((items) => {
        if (active) setTracks(items);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [propertyId]);
  useEffect(() => {
    if (!duration) return;
    const controller = new AbortController();
    void reelFilmstrip(selectedSource, duration, controller.signal)
      .then(setFrames)
      .catch(() => {});
    return () => controller.abort();
  }, [selectedSource, duration]);
  useEffect(() => {
    const file = draft.audioFile;
    const url = file
      ? URL.createObjectURL(file)
      : draft.audio?.kind === 'media'
        ? `/api/media/${draft.audio.mediaId}/file`
        : (tracks.find((track) => draft.audio?.kind === 'track' && track.id === draft.audio.id)
            ?.url ?? '');
    if (audio.current) {
      if (url) audio.current.src = corsSource(url);
      else audio.current.removeAttribute('src');
    }
    return () => {
      if (file) URL.revokeObjectURL(url);
    };
  }, [draft.audioFile, draft.audio, tracks]);
  useEffect(() => {
    const node = cover.current;
    if (node && node.readyState >= 2) {
      if (Math.abs(node.currentTime * 1000 - coverPoint.sourceMs) < 0.5) capture();
      else node.currentTime = coverPoint.sourceMs / 1000;
    }
  }, [coverPoint.sourceMs, coverSource, coverOpacity]);
  useEffect(() => {
    const node = video.current;
    if (node && node.readyState >= 2) {
      node.currentTime = currentClip.trim.startMs / 1000;
    }
  }, [currentClip.id, currentClip.trim.startMs, source, tool]);
  useEffect(() => {
    let active = true;
    const urls: string[] = [];
    const timed = new Set(draft.layerTimings?.map((t) => t.layerId));
    const groups = [
      {
        layers: draft.layers.filter((l) => !timed.has(l.id)),
        layerId: undefined as string | undefined,
      },
      ...(draft.layerTimings ?? []).map((t) => ({
        layers: draft.layers.filter((l) => l.id === t.layerId),
        layerId: t.layerId,
      })),
    ].filter((g) => g.layers.length);
    void Promise.all(
      groups.map(async (group) => {
        const blob = await exportOverlay(group.layers);
        if (!active) return null;
        const url = URL.createObjectURL(blob);
        urls.push(url);
        return { url, layerId: group.layerId };
      }),
    )
      .then((items) => {
        if (active)
          setPreviewOverlay({
            layers: draft.layers,
            timings: draft.layerTimings,
            items: items.filter((item): item is NonNullable<typeof item> => item !== null),
          });
      })
      .catch(() => {
        if (active) setError('Could not preview text. Try reopening the editor.');
      });
    return () => {
      active = false;
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [draft.layers, draft.layerTimings]);
  const stop = () => {
    video.current?.pause();
    setPlaying(false);
    if (audio.current) {
      audio.current.pause();
      audio.current.currentTime = 0;
    }
  };
  const syncAudio = (play: boolean, clock = outputMs) => {
    const node = audio.current;
    const sound = draft.sound ?? defaultReelSound(replacement);
    const fade = Math.min(1, Math.max(0, (total - clock) / 1000));
    if (audioGraph.current)
      audioGraph.current.levels(sound.originalVolume, sound.musicVolume * fade);
    else {
      if (video.current) video.current.volume = sound.originalVolume;
      if (node) node.volume = sound.musicVolume * fade;
    }
    if (!node || !replacement) return;
    const offset = (clock + sound.musicStartMs) / 1000;
    if (Number.isFinite(node.duration) && offset >= node.duration) {
      node.currentTime = node.duration;
      node.pause();
      return;
    }
    if (Math.abs(node.currentTime - offset) > 0.3 || !play) node.currentTime = offset;
    if (play && node.paused)
      void node
        .play()
        .catch(() => setError('Could not play this audio. Choose another file or None.'));
  };
  const tick = () => {
    const node = video.current;
    if (!node || node.paused) return;
    const index = playing ? playIndex : clips.findIndex((c) => c.id === currentClip.id);
    const offset = sequenceDuration(clips.slice(0, Math.max(0, index)));
    const clock = offset + Math.max(0, node.currentTime * 1000 - currentClip.trim.startMs);
    if (node.currentTime * 1000 >= currentClip.trim.endMs) {
      if (index < clips.length - 1) {
        setPlayIndex(index + 1);
        setOutputMs(offset + currentClip.trim.endMs - currentClip.trim.startMs);
      } else {
        node.currentTime = clips[0]!.trim.startMs / 1000;
        setPlayIndex(0);
        setOutputMs(0);
        syncAudio(true, 0);
      }
      return;
    }
    setOutputMs(clock);
    syncAudio(true, clock);
  };
  useEffect(() => {
    if (!playing) return;
    let handle = 0;
    const frame = () => {
      tick();
      handle = requestAnimationFrame(frame);
    };
    handle = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(handle);
  });
  const changeClips = (next: ReelClip[]) => {
    stop();
    const result = reconcileSequence(enterSequence(draftRef.current), next);
    updateDraft({
      ...result.draft,
      clipFiles: Object.fromEntries(
        Object.entries(result.draft.clipFiles ?? {}).filter(([id]) =>
          next.some((c) => c.id === id),
        ),
      ),
      clipInfo: Object.fromEntries(
        Object.entries(result.draft.clipInfo ?? {}).filter(([id]) => next.some((c) => c.id === id)),
      ),
    });
    setNotice(result.notice);
    if (!next.some((c) => c.id === clipId)) setClipId(next[0]!.id);
  };
  const uploadClip = async (id: string) => {
    const file = draftRef.current.clipFiles?.[id];
    if (!file) return;
    setStatuses((current) => ({ ...current, [id]: 'Uploading…' }));
    try {
      const media = (await uploadFile(propertyId, file)).media;
      if (!alive.current) return;
      const current = draftRef.current;
      const clipFiles = { ...current.clipFiles };
      delete clipFiles[id];
      updateDraft({
        ...current,
        clipFiles,
        clips: current.clips?.map((c) =>
          c.id === id ? { ...c, source: { kind: 'media', mediaId: media.id } } : c,
        ),
      });
      setStatuses((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });
    } catch (error) {
      if (alive.current)
        setStatuses((current) => ({ ...current, [id]: `Upload failed: ${errorMessage(error)}` }));
    }
  };
  const addClips = async (files: File[]) => {
    setPreparing(true);
    stop();
    setError(null);
    try {
      if (effectiveClips(draftRef.current).length + files.length > 10)
        throw new Error('Use at most 10 clips.');
      for (const file of files) {
        const ms = await readReelClip(file);
        if (!alive.current) return;
        const current = enterSequence(draftRef.current);
        checkReelSize(current, file.size);
        const id = crypto.randomUUID();
        const result = reconcileSequence(current, [
          ...current.clips!,
          {
            id,
            source: { kind: 'media', mediaId: `pending-${id}` },
            trim: { startMs: 0, endMs: Math.min(ms, 90000) },
          },
        ]);
        updateDraft({
          ...result.draft,
          clipFiles: { ...current.clipFiles, [id]: file },
          clipInfo: {
            ...current.clipInfo,
            [id]: { name: file.name, durationMs: ms, size: file.size },
          },
        });
        setNotice(result.notice);
        await uploadClip(id);
      }
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      if (alive.current) setPreparing(false);
    }
  };
  const moveTrim = (edge: 'startMs' | 'endMs', value: number) => {
    stop();
    if (draft.clips) {
      const trim = {
        ...selectedClip.trim,
        [edge]: Math.round(
          edge === 'startMs'
            ? Math.max(0, Math.min(selectedClip.trim.endMs - 1000, value))
            : Math.min(duration, Math.max(selectedClip.trim.startMs + 1000, value)),
        ),
      };
      changeClips(clips.map((c) => (c.id === selectedClip.id ? { ...c, trim } : c)));
    } else {
      const trim = clampTrim(draft.trim, edge, value, duration);
      const nextTotal = trim.endMs - trim.startMs;
      updateDraft({
        ...draft,
        trim,
        coverMs: Math.max(trim.startMs, Math.min(trim.endMs - 1, draft.coverMs)),
        ...(draft.layerTimings
          ? {
              layerTimings: draft.layerTimings.map((t) => ({
                ...t,
                startMs: Math.min(t.startMs, nextTotal - 1),
                endMs: Math.min(t.endMs, nextTotal),
              })),
            }
          : {}),
      });
      if (draft.layerTimings?.some((t) => t.endMs > nextTotal))
        setNotice('Text times were adjusted to fit the new length.');
    }
  };
  const capture = () => {
    try {
      if (cover.current) {
        const next = reelFrame(cover.current, 270, coverOpacity);
        if (next) setFrame(next);
      }
    } catch {
      setError('Could not read this cover frame. Reopen the Reel to try again.');
    }
  };
  const picture = {
    ...newCardEdit(
      { kind: 'url', url: frame || version.coverUrl || version.imageUrl || source },
      '9:16',
    ),
    layers:
      tool === 'Cover'
        ? [
            ...draft.layers.filter(
              (layer) => !draft.layerTimings?.some((t) => t.layerId === layer.id),
            ),
            ...(draft.layerTimings ?? [])
              .filter((t) => coverOutput >= t.startMs && coverOutput < t.endMs)
              .flatMap((t) => draft.layers.filter((layer) => layer.id === t.layerId)),
          ]
        : draft.layers,
  };
  const done = async () => {
    stop();
    // Nothing changed: closing is the whole job. A render would only make a twin version.
    if (sameReelEdit(draft, reelDraft(version), duration)) {
      onCancel();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onPreserve(draft);
      const job = await renderReel(postId, propertyId, version, draft, (next) => {
        updateDraft(next);
        onPreserve(next);
      });
      onAccepted(job);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Overlay className="sheet-backdrop composer-backdrop">
      <div
        className="composer-sheet reel-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Edit Reel"
        tabIndex={-1}
        ref={dialog}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !busy) {
            stop();
            onCancel();
          }
          if (e.key === 'Tab') {
            const controls = Array.from(
              dialog.current?.querySelectorAll<HTMLElement>(
                'button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),summary',
              ) ?? [],
            ).filter((node) => node.getClientRects().length > 0);
            const first = controls[0],
              last = controls.at(-1);
            if (e.shiftKey && document.activeElement === first) {
              e.preventDefault();
              last?.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
              e.preventDefault();
              first?.focus();
            }
          }
        }}
      >
        <header className="composer-sheet-header">
          <strong>Edit Reel</strong>
          <span>
            {tool === 'Text'
              ? 'Add text and choose when it appears.'
              : tool === 'Cover'
                ? 'Choose the frame people see before playing.'
                : tool === 'Captions'
                  ? 'Add captions or import an SRT file, then review the words.'
                  : tool === 'Music'
                    ? 'Choose sound for your Reel.'
                    : 'Order your clips into a 3–90 second Reel.'}
          </span>
        </header>
        <div className="reel-stage">
          <video
            ref={cover}
            src={corsSource(coverSource)}
            crossOrigin="anonymous"
            preload="auto"
            muted
            playsInline
            hidden
            onLoadedData={() => {
              if (cover.current) {
                cover.current.currentTime = coverPoint.sourceMs / 1000;
                capture();
              }
            }}
            onSeeked={capture}
          />
          <div
            className="reel-preview"
            style={tool === 'Text' || tool === 'Cover' ? { display: 'none' } : undefined}
          >
            <div className="reel-video-frame">
              <video
                aria-label="Reel preview"
                ref={video}
                src={corsSource(source)}
                crossOrigin="anonymous"
                preload="auto"
                poster={version.coverUrl ?? undefined}
                muted={(draft.sound ?? defaultReelSound(replacement)).originalVolume === 0}
                playsInline
                style={{ opacity: fadeOpacity(clips, outputMs, draft.transition) }}
                onLoadedMetadata={(e) => {
                  const ms = Math.floor(e.currentTarget.duration * 1000);
                  if (!Number.isFinite(ms) || ms < (draft.clips ? 1000 : 3000)) {
                    setError('Choose a readable MP4 at least one second long.');
                    return;
                  }
                  sourceDurations.current[source] = ms;
                  if (currentClip.id === selectedClip.id) setDuration(ms);
                  if (!draft.clips) {
                    const endMs = Math.min(ms, draft.trim.endMs),
                      startMs = Math.max(0, Math.min(draft.trim.startMs, endMs - 3000));
                    updateDraft({
                      ...draft,
                      trim: { startMs, endMs },
                      coverMs: Math.max(startMs, Math.min(endMs - 1, draft.coverMs)),
                    });
                  }
                  e.currentTarget.currentTime = currentClip.trim.startMs / 1000;
                }}
                onLoadedData={(e) => {
                  e.currentTarget.currentTime = currentClip.trim.startMs / 1000;
                  if (playing)
                    void e.currentTarget.play().catch(() => setError('Could not play this clip.'));
                }}
                onError={() =>
                  setError('Could not load the original video. Reopen the Reel to try again.')
                }
                onPlay={() => {
                  setPlaying(true);
                  syncAudio(true);
                }}
                onPause={() => {
                  if (audio.current) audio.current.pause();
                }}
                onTimeUpdate={tick}
                onEnded={() => {
                  if (playIndex < clips.length - 1) setPlayIndex(playIndex + 1);
                  else {
                    setPlayIndex(0);
                    if (video.current) {
                      video.current.currentTime = clips[0]!.trim.startMs / 1000;
                      void video.current.play().catch(() => {});
                    }
                    syncAudio(true, 0);
                  }
                }}
              />
              {previewOverlay?.layers === draft.layers &&
                previewOverlay.timings === draft.layerTimings &&
                previewOverlay.items.map((item) => {
                  const timing = item.layerId
                    ? draft.layerTimings?.find((t) => t.layerId === item.layerId)
                    : null;
                  return (
                    <img
                      key={item.url}
                      className="reel-preview-overlay"
                      src={item.url}
                      alt="Reel text overlay"
                      style={{
                        visibility:
                          !timing || (outputMs >= timing.startMs && outputMs < timing.endMs)
                            ? 'visible'
                            : 'hidden',
                      }}
                    />
                  );
                })}
            </div>
            <button
              type="button"
              className="btn reel-play"
              disabled={!duration || busy}
              onClick={() => {
                if (playing) stop();
                else if (video.current) {
                  setPlayIndex(0);
                  setOutputMs(0);
                  setPlaying(true);
                  if (audio.current && typeof AudioContext !== 'undefined') {
                    try {
                      audioGraph.current ??= reelAudioPreview(video.current, audio.current);
                      void audioGraph.current.resume();
                    } catch {
                      setNotice(
                        'Sound preview is limited in this browser; saved sound levels still apply to the render.',
                      );
                    }
                  }
                  video.current.currentTime = clips[0]!.trim.startMs / 1000;
                  syncAudio(true, 0);
                  void video.current
                    .play()
                    .catch(() => setError('Could not play this clip. Try again.'));
                }
              }}
            >
              {playing ? 'Pause preview' : 'Play preview'}
            </button>
          </div>
          {(tool === 'Text' || tool === 'Cover') &&
            (frame ? (
              <PictureStage
                edit={picture}
                onChange={(next) => patchLayers(next.layers)}
                tool="Text"
                selected={tool === 'Text' ? selected : null}
                onSelect={setSelected}
                editing={tool === 'Text' ? editing : null}
                onEditing={setEditing}
                color={color}
                strokeWidth={0.012}
                disabled={busy || tool === 'Cover'}
              />
            ) : (
              <div className="picture-stage">
                <span className="composer-hint">Loading cover frame…</span>
              </div>
            ))}
        </div>
        <div className="reel-side">
          <nav className="picture-tool-tabs" aria-label="Reel tools">
            {(['Clips', 'Text', 'Captions', 'Cover', 'Music'] as const).map((name) => (
              <button
                type="button"
                className="chip hv"
                key={name}
                disabled={busy}
                aria-pressed={tool === name}
                onClick={() => {
                  stop();
                  setEditing(null);
                  setTool(name);
                }}
              >
                {name}
              </button>
            ))}
          </nav>
          {tool === 'Clips' && (
            <>
              <ReelClips
                draft={draft}
                selected={selectedClip.id}
                onSelect={(id) => {
                  stop();
                  setClipId(id);
                  setDuration(
                    sourceDurations.current[
                      localClips[id] ?? clipSource(clips.find((c) => c.id === id)!)
                    ] ??
                      draft.clipInfo?.[id]?.durationMs ??
                      0,
                  );
                }}
                onAdd={(files) => void addClips(files)}
                onClips={changeClips}
                statuses={statuses}
                onRetry={(id) => void uploadClip(id)}
                disabled={busy || preparing}
              />
              <div className="reel-trim">
                <div className="reel-filmstrip" aria-label="Original video timeline">
                  {frames.map((url, i) => (
                    <img key={i} src={url} alt="" />
                  ))}
                  <div
                    className="reel-selection"
                    style={{
                      left: `${(selectedClip.trim.startMs / (duration || 90000)) * 100}%`,
                      width: `${((selectedClip.trim.endMs - selectedClip.trim.startMs) / (duration || 90000)) * 100}%`,
                    }}
                  />
                  {(['startMs', 'endMs'] as const).map((edge) => (
                    <input
                      key={edge}
                      className="reel-handle"
                      aria-label={edge === 'startMs' ? 'Trim start' : 'Trim end'}
                      type="range"
                      min={0}
                      max={duration || 90000}
                      step={100}
                      value={selectedClip.trim[edge]}
                      disabled={!duration || busy}
                      onChange={(e) => moveTrim(edge, Number(e.target.value))}
                    />
                  ))}
                </div>
                <div className="picture-row">
                  {(['startMs', 'endMs'] as const).map((edge) => (
                    <label key={edge}>
                      {edge === 'startMs' ? 'Start' : 'End'}{' '}
                      <input
                        aria-label={edge === 'startMs' ? 'Start seconds' : 'End seconds'}
                        type="number"
                        min={0}
                        max={duration / 1000}
                        step={0.1}
                        key={`${edge}-${selectedClip.trim[edge]}`}
                        defaultValue={selectedClip.trim[edge] / 1000}
                        disabled={!duration || busy}
                        onBlur={(e) => {
                          moveTrim(edge, Number(e.currentTarget.value) * 1000);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.currentTarget.blur();
                        }}
                      />
                    </label>
                  ))}
                  <output>{stamp(total)} total · 3–90 seconds</output>
                </div>
                {duration > 90000 && (
                  <span className="composer-hint">
                    Your first draft uses the first 90 seconds. Move the handles to choose any later
                    part of the original.
                  </span>
                )}
              </div>
              <label className="picture-tools">
                Between clips{' '}
                <select
                  aria-label="Transition"
                  value={draft.transition ?? 'cut'}
                  disabled={busy}
                  onChange={(e) =>
                    updateDraft({ ...draft, transition: e.target.value as 'cut' | 'fade' })
                  }
                >
                  <option value="cut">Cut</option>
                  <option value="fade">Fade</option>
                </select>
              </label>
            </>
          )}
          {tool === 'Text' && (
            <PictureTools
              reel
              tool="Text"
              edit={picture}
              onChange={(next) => patchLayers(next.layers)}
              selected={selected}
              onSelect={setSelected}
              editing={editing}
              onEditing={setEditing}
              headline={version.headline}
              color={color}
              onColor={setColor}
              strokeWidth={0.012}
              onWidth={() => {}}
              disabled={busy}
            />
          )}
          {tool === 'Captions' && (
            <ReelCaptions
              draft={draft}
              duration={total}
              selected={selected}
              onSelect={setSelected}
              onChange={updateDraft}
              onError={setError}
              disabled={busy}
            />
          )}
          {(tool === 'Text' || tool === 'Captions') && (
            <ReelTextTiming
              draft={draft}
              selected={selected}
              duration={total}
              onChange={updateDraft}
              disabled={busy}
            />
          )}
          {tool === 'Cover' && (
            <label className="reel-cover">
              Cover at {stamp(draft.coverMs)}
              <input
                aria-label="Cover frame"
                type="range"
                min={draft.trim.startMs}
                max={draft.trim.endMs - 1}
                step={1}
                value={draft.coverMs}
                disabled={!duration || busy}
                onChange={(e) => setDraft({ ...draft, coverMs: Number(e.target.value) })}
              />
            </label>
          )}
          {tool === 'Music' && (
            <div className="picture-tools">
              <ReelSound
                draft={draft}
                onChange={(next) => {
                  stop();
                  updateDraft(next);
                }}
                disabled={busy}
              />
              <div className="picture-row">
                <button
                  type="button"
                  className="chip hv"
                  disabled={busy}
                  aria-pressed={!replacement}
                  onClick={() => {
                    stop();
                    setDraft({ ...draft, audio: null, audioFile: undefined, audioSize: undefined });
                  }}
                >
                  None
                </button>
                <button
                  type="button"
                  className="chip hv"
                  disabled={busy}
                  onClick={() => audioInput.current?.click()}
                >
                  Upload audio
                </button>
                <button
                  type="button"
                  className="chip hv"
                  disabled={busy || making}
                  onClick={() => {
                    stop();
                    setError(null);
                    setMaking(true);
                    void generateReelMusic(postId)
                      .then((made) => {
                        setTracks((prior) => [
                          {
                            id: made.id,
                            title: made.label || 'Soundtrack',
                            mood: 'Generated',
                            moodId: null,
                            durationSeconds: made.durationSeconds ?? 0,
                            url: `/api/media/${made.id}/file`,
                            source: 'generated',
                            postId,
                          },
                          ...prior,
                        ]);
                        setDraft({
                          ...draft,
                          audio: { kind: 'media', mediaId: made.id },
                          audioFile: undefined,
                          audioSize: undefined,
                        });
                      })
                      .catch((e) => setError(errorMessage(e)))
                      .finally(() => setMaking(false));
                  }}
                >
                  {making ? 'Making music…' : 'Generate music'}
                </button>
                <input
                  ref={audioInput}
                  hidden
                  aria-label="Upload audio"
                  type="file"
                  accept="audio/mpeg,audio/mp4,audio/wav,.m4a,.mp3,.wav"
                  disabled={busy}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file)
                      try {
                        checkReelAudio(file);
                        checkReelSize(draft, 0, file.size);
                        stop();
                        setError(null);
                        setDraft({ ...draft, audio: null, audioFile: file, audioSize: file.size });
                      } catch (e) {
                        setError(errorMessage(e));
                      }
                  }}
                />
              </div>
              <span className="composer-hint">
                {replacement
                  ? `${draft.audioFile?.name ?? (draft.audio?.kind === 'media' ? (tracks.find((t) => draft.audio?.kind === 'media' && t.id === draft.audio.mediaId)?.title ?? 'Your audio') : 'Music')} · Fades out at the end.`
                  : 'None keeps the original clip audio.'}
              </span>
              {[...new Set(tracks.map((track) => track.mood))].map((mood) => (
                <div key={mood}>
                  <strong>{mood}</strong>
                  <div className="picture-row">
                    {tracks
                      .filter((track) => track.mood === mood)
                      .map((track) => (
                        <button
                          type="button"
                          className="chip hv"
                          key={track.id}
                          disabled={busy}
                          aria-pressed={
                            draft.audio?.kind === 'media' && draft.audio.mediaId === track.id
                          }
                          onClick={() => {
                            stop();
                            setDraft({
                              ...draft,
                              audio: { kind: 'media', mediaId: track.id },
                              audioFile: undefined,
                              audioSize: undefined,
                            });
                          }}
                        >
                          {track.title}
                        </button>
                      ))}
                  </div>
                </div>
              ))}
              <span className="composer-hint">Use audio you have the right to publish.</span>
            </div>
          )}
          <audio
            ref={audio}
            crossOrigin="anonymous"
            aria-label="Replacement audio"
            preload="auto"
            onError={() => setError('Could not load this audio. Choose another file or None.')}
          />
          {notice && (
            <span className="composer-hint" role="status">
              {notice}
            </span>
          )}
          {(total < 3000 || total > 90000) && (
            <ErrorText>Choose 3–90 seconds total. Trim or remove a clip to continue.</ErrorText>
          )}
          <ErrorText>{error}</ErrorText>
          <footer className="composer-sheet-footer">
            <button
              type="button"
              className="btn hv"
              disabled={busy}
              onClick={() => {
                stop();
                onCancel();
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary hv"
              disabled={
                busy ||
                preparing ||
                !duration ||
                total < 3000 ||
                total > 90000 ||
                Object.keys(draft.clipFiles ?? {}).length > 0
              }
              onClick={() => void done()}
            >
              {busy ? 'Preparing…' : 'Done'}
            </button>
          </footer>
        </div>
      </div>
    </Overlay>
  );
}
